import { Box, Button, Checkbox, Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LocalSpinner from "../../../../components/LocalSpinner";
import { useGetQuarterlyEmissions } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { useGetQuarterlyCompanyTotalEmissions } from "../../../../hooks/query/reports";
import { scopeThreeCategories } from "../../CompanyEmissionsDashboard/components/helpers";
import YoYChip from "./YoYChip";

// Table view of the Total Emissions summary-card figures, split by quarter
// with a whole-year total column. Always shows the full year regardless of
// the page's quarter filter — the quarters ARE the columns here.
//
// Data sources mirror the cards exactly: scope totals per period come from
// the dimension-filtered /insights/total endpoint; the Scope 3 category
// breakdown comes from /reports/total-emissions (org-wide, like the Scope 3
// card's category list).
//
// Each cell carries a % variance chip (same YoYChip lozenge as the cards):
// vs the previous quarter (Q2–Q4 only), or — via the toggle — vs the same
// quarter of the previous year. In the latter mode the year-total column
// compares against the previous year's total.

// Q1..Q4 come from per-quarter requests; "Yearly Entry" is derived (year
// total minus the quarter sum — data entered at the yearly level that
// belongs to no individual quarter); "Year" is the whole-year request.
const PERIOD_LABELS = ["Q1", "Q2", "Q3", "Q4", "Yearly Entry", "Year"];
const YEARLY_ENTRY_IDX = 4;
const YEAR_IDX = 5;

const COMPARE_OPTIONS = [
  { label: "vs previous quarter", value: "qoq" },
  { label: "vs same quarter last year", value: "yoy" },
];

// One cell: a number rendered like the Scope 3 card's category list, or an
// en-dash when the period has no report / no value.
const formatCell = (value) =>
  typeof value === "number" && value !== 0
    ? value.toLocaleString("en-GB", { maximumFractionDigits: 2 })
    : "–";

// % change between two period values; null (no chip) when either side is
// missing/zero — same rule the summary cards use.
const pctChange = (current, previous) => {
  if (typeof current !== "number" || current === 0) return null;
  if (typeof previous !== "number" || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

// Expand a row's fetched values [Q1..Q4, year] to the display columns
// [Q1..Q4, yearlyEntry, year]. Annual-only = year total − quarter sum: the
// portion entered at the yearly level, attributable to no quarter. Tiny
// residues (float noise) render as a dash rather than a spurious figure.
const withYearlyEntry = (values) => {
  const [q1, q2, q3, q4, yearTotal] = values;
  const quarters = [q1, q2, q3, q4];
  let yearlyEntry = null;
  if (typeof yearTotal === "number") {
    const qSum = quarters.reduce((acc, v) => acc + (typeof v === "number" ? v : 0), 0);
    const diff = yearTotal - qSum;
    if (diff > 0.005) yearlyEntry = diff;
  }
  return [q1, q2, q3, q4, yearlyEntry, yearTotal];
};

// Variance for display column i (0..3 = Q1..Q4, 4 = annual-only, 5 = year
// total) given this row's values for the current and previous year.
const varianceFor = (values, prevYearValues, i, compareMode) => {
  if (compareMode === "qoq") {
    // Previous-quarter comparison: only meaningful between real quarters —
    // nothing to compare Q1, the annual-only bucket, or the total against.
    if (i === 0 || i >= YEARLY_ENTRY_IDX) return null;
    return pctChange(values[i], values[i - 1]);
  }
  // Same period previous year (annual-only and year total compare to last
  // year's equivalents).
  return pctChange(values[i], prevYearValues?.[i]);
};

const CompareToggle = ({ mode, setMode }) => (
  <Flex bg="gray.100" borderRadius="full" p="3px">
    {COMPARE_OPTIONS.map((option) => {
      const active = mode === option.value;
      return (
        <Button
          key={option.value}
          size="xs"
          borderRadius="full"
          bg={active ? "pureBlack" : "transparent"}
          color={active ? "white" : "gray.500"}
          fontWeight={active ? "600" : "normal"}
          onClick={() => setMode(option.value)}
          _hover={{ bg: active ? "pureBlack" : "gray.200" }}
          px={3}
        >
          {option.label}
        </Button>
      );
    })}
  </Flex>
);

// Value + variance chip cell body. Number first, chip to its right — keeps
// every row a single line so the table stays compact. A fixed-width slot
// reserves the chip space even when a cell has no chip, so the numbers in a
// column all end at the same x and stay scannable. With comparisons off
// (showSlot=false) the slot collapses so the columns tighten up.
const CellValue = ({ value, variance, bold, showSlot }) => (
  <Flex justify="flex-end" align="center" gap={2}>
    <Text fontWeight={bold ? "600" : "inherit"}>{formatCell(value)}</Text>
    {showSlot && (
      <Flex w="76px" justify="flex-start" flexShrink={0}>
        {variance != null && <YoYChip pctChange={variance} />}
      </Flex>
    )}
  </Flex>
);

// Category-column label that links to a drill-down page. Same navigation as
// clicking a summary card: sets ?detailView= (and ?category= for a Scope 3
// category) on the current URL, so year/dimension context is preserved.
const DrillLink = ({ children, onClick, indent = false }) => (
  <Text
    as="button"
    type="button"
    onClick={onClick}
    textAlign="left"
    fontSize={indent ? "body.xs" : "inherit"}
    fontWeight="inherit"
    color="inherit"
    textDecoration="underline"
    textDecorationColor="gray.300"
    textUnderlineOffset="3px"
    _hover={{ color: "darkTeal", textDecorationColor: "darkTeal" }}
  >
    {children}
  </Text>
);

const QuarterlyEmissionsTable = ({ year, dimensionParams }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning.modal.categories",
  });
  const [searchParams, setSearchParams] = useSearchParams();

  // Navigate to a drill-down page, keeping the year/dimension params intact.
  const openDetail = (detailView, categoryNumberKey) => {
    const params = new URLSearchParams(searchParams);
    params.set("detailView", detailView);
    if (categoryNumberKey) params.set("category", categoryNumberKey);
    else params.delete("category");
    setSearchParams(params);
  };
  // Master switch for the variance chips; the mode toggle only matters (and
  // previous-year data is only fetched) while comparisons are on.
  const [showComparison, setShowComparison] = useState(false);
  const [compareMode, setCompareMode] = useState("qoq");
  const isYoY = showComparison && compareMode === "yoy";

  // Five queries each: Q1..Q4 + whole year. Quarters without a report 404 —
  // retry:false lets them settle quickly and render as "–".
  const emissionQueries = useGetQuarterlyEmissions(year, dimensionParams, {
    retry: false,
    cacheTime: 60_000,
  });
  const scope3Queries = useGetQuarterlyCompanyTotalEmissions(year, {
    retry: false,
    cacheTime: 60_000,
  });

  // Previous-year periods, only fetched in same-quarter-last-year mode.
  const prevEmissionQueries = useGetQuarterlyEmissions(year - 1, dimensionParams, {
    retry: false,
    cacheTime: 60_000,
    enabled: isYoY,
  });
  const prevScope3Queries = useGetQuarterlyCompanyTotalEmissions(year - 1, {
    retry: false,
    cacheTime: 60_000,
    enabled: isYoY,
  });

  const isLoading =
    emissionQueries.some((q) => q.isLoading) ||
    scope3Queries.some((q) => q.isLoading) ||
    (isYoY && (prevEmissionQueries.some((q) => q.isLoading) || prevScope3Queries.some((q) => q.isLoading)));

  // Per-period payloads, index 0..3 = Q1..Q4, index 4 = whole year.
  const periods = emissionQueries.map((q) => (q.status === "success" ? q.data : null));
  const scope3Periods = scope3Queries.map((q) => (q.status === "success" ? q.data : null));
  const prevPeriods = prevEmissionQueries.map((q) => (q.status === "success" ? q.data : null));
  const prevScope3Periods = prevScope3Queries.map((q) => (q.status === "success" ? q.data : null));

  // `detailView` links the label to a drill-down page (Scope 1/2/3). The
  // "Scope 1 and 2 Total" rows have no drill-down page, so no link.
  const scopeRows = [
    { label: "Scope 1", get: (d) => d?.scope1Total, detailView: "scopeOne" },
    { label: "Scope 2 (Location)", get: (d) => d?.scope2LocationTotal, detailView: "scopeTwo" },
    { label: "Scope 2 (Market)", get: (d) => d?.scope2MarketTotal, detailView: "scopeTwo" },
    { label: "Scope 1 and 2 Total (Location)", get: (d) => d?.scope1AndScope2LocationTotal },
    { label: "Scope 1 and 2 Total (Market)", get: (d) => d?.scope1AndScope2MarketTotal },
    { label: "Scope 3", get: (d) => d?.scope3Total, bold: true, detailView: "scopeThree" },
  ].map((row) => ({
    ...row,
    values: withYearlyEntry(periods.map((d) => row.get(d))),
    prevValues: withYearlyEntry(prevPeriods.map((d) => row.get(d))),
  }));

  // Scope 3 category rows — only categories with a value in at least one
  // period, matching the card's "hide empty categories" behaviour.
  const categoryRows = scopeThreeCategories
    .map((cat) => ({
      key: cat.numberKey,
      label: `${cat.index}. ${t(cat.translationKey)}`,
      values: withYearlyEntry(scope3Periods.map((d) => d?.[`category${cat.numberKey}TotalCo2Emissions`])),
      prevValues: withYearlyEntry(prevScope3Periods.map((d) => d?.[`category${cat.numberKey}TotalCo2Emissions`])),
    }))
    .filter((row) => row.values.some((v) => typeof v === "number" && v > 0));

  return (
    <Box>
      <Flex justify="space-between" align="flex-end" flexWrap="wrap" gap={3}>
        <Box>
          <Text fontSize="body.m" fontWeight="600" color="gray.700">
            Emissions by quarter
          </Text>
          <Text fontSize="body.xs" color="gray.500" mb={3}>
            All figures in tCO2e for {year}, split by quarter with a full-year total. Scope totals
            respect the dimension filter; the Scope 3 category breakdown is organisation-wide.
          </Text>
        </Box>
        <Flex align="center" gap={3} mb={3}>
          <Checkbox
            colorScheme="limeShade"
            size="sm"
            isChecked={showComparison}
            onChange={(e) => setShowComparison(e.target.checked)}
          >
            <Text fontSize="body.xs" color="gray.600">
              Compare
            </Text>
          </Checkbox>
          {showComparison && <CompareToggle mode={compareMode} setMode={setCompareMode} />}
        </Flex>
      </Flex>
      {isLoading ? (
        <Flex justify="center" py={10}>
          <LocalSpinner size="md" color="darkTeal" />
        </Flex>
      ) : (
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th color="gray.600" py={3}>
                  Category
                </Th>
                {PERIOD_LABELS.map((label) => (
                  <Th key={label} isNumeric color="gray.600" py={3}>
                    {/* Mirror CellValue's fixed chip slot so the header label
                        right-aligns with the numbers, not the chip column. */}
                    <Flex justify="flex-end" align="center" gap={2}>
                      <Text as="span">{label === "Year" ? `${year} Total` : label}</Text>
                      {showComparison && <Box w="76px" flexShrink={0} />}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {scopeRows.map((row) => (
                <Tr key={row.label}>
                  <Td fontWeight={row.bold ? "600" : "normal"} color="gray.700">
                    {row.detailView ? (
                      <DrillLink onClick={() => openDetail(row.detailView)}>{row.label}</DrillLink>
                    ) : (
                      row.label
                    )}
                  </Td>
                  {row.values.map((v, i) => (
                    <Td key={PERIOD_LABELS[i]} isNumeric fontWeight={i === YEAR_IDX ? "600" : "normal"} color="gray.800">
                      <CellValue
                        value={v}
                        variance={showComparison ? varianceFor(row.values, row.prevValues, i, compareMode) : null}
                        bold={i === YEAR_IDX}
                        showSlot={showComparison}
                      />
                    </Td>
                  ))}
                </Tr>
              ))}
              {categoryRows.map((row) => (
                <Tr key={row.key}>
                  <Td pl={8} fontSize="body.xs" color="gray.600">
                    <DrillLink indent onClick={() => openDetail("scopeThreeCategory", row.key)}>
                      {row.label}
                    </DrillLink>
                  </Td>
                  {row.values.map((v, i) => (
                    <Td
                      key={PERIOD_LABELS[i]}
                      isNumeric
                      fontSize="body.xs"
                      fontWeight={i === YEAR_IDX ? "600" : "normal"}
                      color="gray.700"
                    >
                      <CellValue
                        value={v}
                        variance={showComparison ? varianceFor(row.values, row.prevValues, i, compareMode) : null}
                        bold={i === YEAR_IDX}
                        showSlot={showComparison}
                      />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      <Text fontSize="body.xs" color="gray.500" mt={2}>
        A dash means no report is available for that period. &ldquo;Yearly Entry&rdquo; is data
        entered at the yearly level that belongs to no individual quarter — the four quarters plus
        this column add up to the year total.{" "}
        {showComparison &&
          (isYoY
            ? `Change chips compare each period with the same period of ${year - 1}; green = lower emissions.`
            : "Change chips compare each quarter with the one before it; green = lower emissions.")}
      </Text>
    </Box>
  );
};

export default QuarterlyEmissionsTable;
